import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container } from 'react-bootstrap';
import { isEmpty, isArray, set, cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import Loader from '../../../Components/Utility/Loader/Loader';
import ProductCostingTabMenu from '../../../Components/GreedyNavBar/ProductCostingTabMenu';
import { useProductCostingState } from './ProductCostingContext';
import ProductCostingComment from './ProductCostingComment/ProductCostingComment';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import noImage from '../../../assets/images/no-image.png';
import ProductImageThumb from '../../../Components/ProductImageThumb';
import ProductCosting from './ProductCosting/ProductCosting';
import { commonPath, averageCostingFieldpath, detailedCostingFieldpath, quantitiesFieldpath } from './fieldPath';
import { resolveCompanyCurrencyValue } from './exchange-rate.helpers';
import { withoutEmptyRequirements } from './specification-requirements.helpers';
import { shortenandHoverString, supplierChangeKeysArray } from '../../../Components/helper';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import './product-costing.css';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';
import {
  fetchProductCostingComments,
  getProductCostingTemplateDetailsByIds,
  updateProductCostingData,
  getProductCostingDetailsById,
  getUnIntegratedSupplierCostingDetailsById,
  addProductCostingData,
  inviteSupplierAction,
  unmountCostingState,
  refreshModule,
  clearAllProductCostingSuggestedHighlights,
  clearedAllStatus,
} from './store/productCosting.actions';
import type { CostingTemplateDetail, ProductCostingDetail, ProductCostingDispatch, TransactionMsg } from './store/productCosting.types';
import useDetailedPageHook from './Hooks/useDetailedPageHook';
import SubmitModal from './Modal/SubmitModal';
import useShowHideSectionHooks from './Hooks/useShowHideSectionHooks';
import useRefreshHooks from './Hooks/useRefreshHooks';
import useExcelDownload from './Hooks/useExcelDownload';
import useDetailedCostingHooks from './Hooks/useDetailedCostingHooks';
import Chat from '../../Chat/Chat';
import type { LandedInfoStateEntry, ProductCostingState } from './types';
import { costingDetailPath, costingListPath } from './product-costing.routes';
import { ClearHighlightsIcon, DownloadIcon, InviteSupplierIcon } from '../../../bombiix-icon';
import { BombiixModal } from '../../../Components/BombiixModal';

/** A colourway/size/length combination as the save payload's extractor walks it. */
interface CostingCombinationRow {
  colourway?: { _id?: string };
  length?: { _id?: string };
  size?: string;
  expandData?: CostingCombinationRow[];
  [key: string]: unknown;
}

/** The `{ colourways, lengths, sizes }` id triple persisted alongside the costing. */
interface ExtractedCombinations {
  colourways: string[];
  lengths: string[];
  sizes: string[];
}

/** A supplier as this page reads it off the costing state. */
interface SupplierRef {
  _id?: string;
  name?: string;
  companies?: { currency?: string };
  [key: string]: unknown;
}

/** The exchange-rate block persisted per distribution centre. */
interface DcExchangeInfo {
  exchangedRate?: string | number | undefined;
  exchangedRateManualFalse?: string | number | undefined;
  companyCurrency?: string | undefined;
  supplierCurrency?: string | undefined;
  companyCurrencyValue?: string | number | undefined;
  manualAdded?: boolean | undefined;
  [key: string]: unknown;
}

/** One entry of the exchange-rate master for the costing's season. */
interface ExchangeRateMasterRow {
  defaultCurrency?: string;
  combinationCurrencies?: Array<{ currency?: string; value?: string | number }>;
  [key: string]: unknown;
}

/** A costing-template column, keyed by `aliasName`. */
interface TemplateColumn {
  _id?: string | undefined;
  aliasName?: string | undefined;
  aliasDisplayName?: string | undefined;
  isDefault?: boolean | undefined;
  value?: unknown;
  [key: string]: unknown;
}

/** The costing template, as this page reads it. */
interface PageTemplateDetail {
  _id?: string;
  isCostingInfoActive?: boolean;
  isAdditionalComponentActive?: boolean;
  isSetUpCostActive?: boolean;
  landedInfo?: TemplateColumn[];
  costingInfo?: TemplateColumn[];
  [key: string]: unknown;
}

/** The save payload this page assembles and posts. */
interface CostingSavePayload {
  [key: string]: unknown;
  productTemplate: PageTemplateDetail;
}

/** The redux slices this page reads. */
interface DetailPageRootState {
  Measuremets: { productName?: { number?: string; description?: string } };
  newProductCostingData: {
    bomCompoList?: unknown[];
    productColours?: Array<{ _id?: string }>;
    productSizes?: string[];
    productLengths?: Array<{ _id?: string }>;
    productExchnageRate?: { exchangeRateList?: ExchangeRateMasterRow[] };
    productCostingDetail?: ProductCostingDetail | null;
    costingTemplateDetail?: PageTemplateDetail;
    isRefreshed?: boolean;
  };
  BOMConfigDetails: {
    productDetails?: {
      image?: string;
      likenessId?: string;
      licenseId?: string;
      licensorId?: string;
      suppliers?: unknown[];
      supplierFactories?: unknown[];
      [key: string]: unknown;
    };
    isProductArchived?: boolean;
  };
  Users: { logInRights?: unknown };
  UserDetails: { isSupplierLoggedIn?: boolean; userInfo?: { userId?: string; firstName?: string; lastName?: string; companyCurrency?: string } };
}

/** The Product Costing detail page: header actions, the two costing tabs, and the comment thread. */
function NewProducCostingDetailPage(): React.JSX.Element {
  const { devOrderId, productid } = useParams<{ devOrderId?: string; productid?: string }>();
  /* Typed dispatch, so the module's own thunks type-check at the call site. */
  const dispatch = useDispatch<ProductCostingDispatch>();
  const history = useHistory();

  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const queryDevOrderId = queryParams.get('devOrderId');
  const queryCostingId = queryParams.get('costingId');
  const querySupplierId = queryParams.get('supplierId');
  /**
   * The non-integrated supplier link carries the costing id in the query string,
   * so `productid` is undefined there. Save must branch on this, otherwise it
   * falls through to the create path and duplicates the costing.
   */
  const effectiveCostingId = productid || queryCostingId;
  const pleaseFillTheRequiredFields = 'Please fill the required fields';
  const isComment = queryParams.get('isComment');
  const { t } = useTranslation('common');
  const hasUserToggledCostingTabRef = useRef(false);
  const [compState, setState] = useState<{
    showHideAlertPopup: boolean;
    selectedTemplate: PageTemplateDetail | null;
    loading: boolean;
    modalLoading: boolean;
    productLoading: boolean;
    tabName: string;
    module: string;
  }>({
    showHideAlertPopup: false,
    selectedTemplate: null,
    loading: false,
    modalLoading: false,
    productLoading: false,
    tabName: 'averageCosting',
    module: 'subModuleActiveTab',
  });
  const {
    defaultTab: { moduleActiveTab, subModuleActiveTab },
    setDefaultTabs,
    productCostingstate,
    setProductCostingState,
    internalState,
    setInternalState,
    landedInfoState,
    landedInfoStates,
    setLandedInfoStates,
    filteredDistributionCentres,
    additionalState,
    testingState,
    grandTotalState,
    colSizeLengthToggleState,
    checkValidation,
    detailedCostingState,
    setDetailedCostingState,
    detailedQuanitiesState,
    setDetailedQuanitiesState,
    setActivateColSizeLengthToggle,
    setTestingState,
    setGrandTotalState,
    loader,
    setCostingLoader,
    setCostingCommentLoader,
    commentLoader,
    initialDetailedCostingState,
    initialDetailedCostingQuanitiesState,
    initialTestingState,
    additionalComponentState,
    specificationRequirementState,
    automatedFreight,
  } = useProductCostingState();
  const { calculateDetailCosting } = useDetailedCostingHooks();

  const {
    manipualtedProductCostingSaveData,
    handleAverageCosting,
    handleQuantities,
    renderDetailedCostingArr,
    handleAdditonalModule,
    handleAdditonalComponentModule,
    handleSpecificationRequirementModule,
    handleLandedInfoModule,
    recalculateLandedInfoForAutomatedFreight,
  } = useDetailedPageHook();
  /* The hook's own row/template interfaces are not exported; borrowing them from its signatures
     keeps these casts pinned to the real contract instead of a copy that could drift. */
  type SaveDataRows = Parameters<typeof manipualtedProductCostingSaveData>[0];
  type LandedTemplateArg = Parameters<typeof handleLandedInfoModule>[0];
  type LandedGrandTotalArg = Parameters<typeof handleLandedInfoModule>[2];
  type LandedIncotermArg = Parameters<typeof handleLandedInfoModule>[3];
  type BomListArg = Parameters<typeof renderDetailedCostingArr>[0];
  type DetailedCalcState = Parameters<typeof calculateDetailCosting>[0];
  const { handleCostingDownload: handleExcelDownload } = useExcelDownload();
  const { hideInviteBtn, isSupplier, isSupplierView, hideSection } = useShowHideSectionHooks();
  const { refreshAllModules } = useRefreshHooks();
  /* UserRights.js declares `CheckActionPermission(rights, section, module = null, action = null)`,
     so TypeScript infers the last two parameters as `null` from their defaults. The cast restores
     the signature the function actually accepts — same shim as ProductCostingContext. */
  const { CheckActionPermission } = useComponent() as {
    CheckActionPermission: (rights: unknown, section: string, module?: string | null, action?: string | null) => boolean;
  };
  const {
    productInfo: { supplierId: supplierIdValue, currency, reference },
    averageCosting: { averageCostingActive },
    productInfo,
    productTemplate: productTemplateValue,
    sampleRef,
    averageCosting,
    status,
    isGlobalUrl,
    costingSizes,
    isAccessDenied,
    landedInformationColumns,
  } = productCostingstate;
  /* The costing document stores populated objects where `ProductInfoState` names ids — the same
     mismatch documented in useDetailedPageHook. Aliased here so the body stays unchanged. */
  const supplierId = supplierIdValue as unknown as SupplierRef | null;
  const productTemplate = productTemplateValue as PageTemplateDetail | null;

  const {
    detailedCosting: { detailedCostingActive, bomTypeArr },
  } = detailedCostingState;

  const { colourWayToggle, sizeLengthToggle } = colSizeLengthToggleState;
  const partiallyComplete = 'Partially Complete';

  const {
    Measuremets: { productName },
    newProductCostingData: {
      bomCompoList,
      productColours,
      productSizes,
      productLengths,
      productExchnageRate,
      productCostingDetail,
      costingTemplateDetail,
      isRefreshed,
    },
    BOMConfigDetails: { productDetails, isProductArchived },
    Users: { logInRights },
    UserDetails: { userInfo },
  } = useSelector((reducer: DetailPageRootState) => reducer);

  const CoastingAddEditRights = CheckActionPermission(logInRights, 'products', 'costing', 'addEdit');

  const SubModuleTabs = [
    {
      eventKey: 'averageCosting',
      eventName: 'Average Costing',
      isDisabled: false,
    },
    {
      eventKey: 'detailedCosting',
      eventName: 'Detailed Costing',
      isDisabled: false,
    },
  ];

  useEffect(() => {
    if (isComment === 'true' && productid) {
      activeTheTab('comments', 'moduleActiveTab');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComment, productid]);

  const activeTheTab = async (tabName: string, module: string): Promise<void> => {
    if (module === 'moduleActiveTab' && tabName === 'comments') {
      setCostingCommentLoader(true);
      const res = await dispatch(fetchProductCostingComments(productid as string));
      if (res) {
        setCostingCommentLoader(false);
      }
      setDefaultTabs((prev) => ({ ...prev, [module]: tabName }));
    } else if (module === 'moduleActiveTab' && tabName === 'productCosting') {
      setDefaultTabs((prev) => ({ ...prev, [module]: tabName }));
    } else if (module === 'subModuleActiveTab' && bomCompoList?.length === 0 && tabName === 'detailedCosting') {
      toasterMsg(t('DEVLOPMENT_ORDER.COSTING_DETAILS.BOM_COMPONENT_PRODUCT'), 'danger');
    } else if (subModuleActiveTab === tabName) {
      /* Selecting the tab that is already active is a no-op, exactly as in the original. */
    } else {
      setState((prevState) => ({
        ...prevState,
        showHideAlertPopup: true,
        tabName,
        module,
      }));
    }
  };
  const imgSrc = (option?: { image?: string }): string => {
    return option?.image || noImage;
  };

  useEffect(() => {
    hasUserToggledCostingTabRef.current = false;
  }, [productCostingDetail]);
  useEffect(() => {
    const callFn = async (): Promise<void> => {
      const firstDcExchangeInfo = landedInfoStates?.[0]?.landedExchangeRateInformation;
      const currencyValue = firstDcExchangeInfo?.manualAdded ? firstDcExchangeInfo?.exchangedRate ?? 1 : internalState?.primaryCurrValue ?? 1;
      if (detailedCostingActive)
        calculateDetailCosting(productCostingDetail as unknown as DetailedCalcState, currencyValue, true, productCostingstate?.productInfo?.currency);
    };
    if (
      productid &&
      devOrderId &&
      productCostingDetail &&
      internalState?.primaryCurrValue &&
      productCostingstate?.productInfo?.currency &&
      !hasUserToggledCostingTabRef.current
    ) {
      callFn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    productid,
    productCostingDetail,
    devOrderId,
    internalState?.primaryCurrValue,
    landedInfoStates?.[0]?.landedExchangeRateInformation?.manualAdded,
    productCostingstate?.productInfo?.currency,
    detailedCostingActive,
  ]);
  useEffect(() => {
    if (isEmpty(supplierId?.companies?.currency)) return;
    const exchangeRateList = productExchnageRate?.exchangeRateList;
    const savedExchangeInfo = (productCostingDetail?.landedInformations?.[0] as { landedExchangeRateInformation?: DcExchangeInfo } | undefined)
      ?.landedExchangeRateInformation;
    const fallbackRate = savedExchangeInfo?.exchangedRateManualFalse ?? savedExchangeInfo?.exchangedRate ?? 1;
    let nextPrimaryCurrValue = fallbackRate;
    if (isArray(exchangeRateList) && exchangeRateList?.length) {
      const matchedDefaultCurrency = exchangeRateList?.find((val) => val?.defaultCurrency === userInfo?.companyCurrency) || exchangeRateList?.[0];
      const findCurrValue = matchedDefaultCurrency?.combinationCurrencies?.find((comb) => comb?.currency === supplierId?.companies?.currency);
      if (findCurrValue?.value) {
        nextPrimaryCurrValue = findCurrValue.value;
      }
    }
    setInternalState((preSatate) => ({
      ...preSatate,
      primaryCurrValue: nextPrimaryCurrValue as number,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productExchnageRate, supplierId?.companies?.currency, currency, productCostingDetail, userInfo?.companyCurrency]);

  const extractCombinationsFromCostingData = (costingInfo?: CostingCombinationRow[]): ExtractedCombinations => {
    const uniqueColourways = new Set<string>();
    const uniqueLengths = new Set<string>();
    const uniqueSizes = new Set<string>();

    if (!costingInfo || !Array.isArray(costingInfo)) {
      return { colourways: [], lengths: [], sizes: [] };
    }

    costingInfo.forEach((item) => {
      if (item?.colourway?._id) {
        uniqueColourways.add(item.colourway._id);
      }

      if (item?.expandData && Array.isArray(item.expandData)) {
        item.expandData.forEach((expandItem) => {
          if (expandItem?.colourway?._id) {
            uniqueColourways.add(expandItem.colourway._id);
          }
          if (expandItem?.length?._id) {
            uniqueLengths.add(expandItem.length._id);
          }
          if (expandItem?.size) {
            uniqueSizes.add(expandItem.size);
          }
        });
      }

      if (item?.length?._id) {
        uniqueLengths.add(item.length._id);
      }
      if (item?.size) {
        uniqueSizes.add(item.size);
      }
    });

    return {
      colourways: Array.from(uniqueColourways),
      lengths: Array.from(uniqueLengths),
      sizes: Array.from(uniqueSizes),
    };
  };

  const refreshDistributionCentresList = async (colourWayToggleCopy?: boolean, sizeLengthToggleCopy?: boolean): Promise<void> => {
    if (!filteredDistributionCentres.length) return;

    const mergedStates: LandedInfoStateEntry[] = filteredDistributionCentres.map((dc) => {
      const existing = landedInfoStates.find((s) => s.distributionCentre?.distributionName === dc.distributionName);
      if (existing) return existing;
      return {
        distributionCentre: dc,
        landedInfo: { landedInfoColumnArr: [], landedInfoArr: [], currency: null, targetCost: '' },
        landedExchangeRateInformation: {},
      } as LandedInfoStateEntry;
    });

    setLandedInfoStates(mergedStates);

    if (costingTemplateDetail?._id) {
      const res = await dispatch(
        getProductCostingTemplateDetailsByIds(costingTemplateDetail._id, devOrderId, {
          colourWayToggle: colourWayToggleCopy,
          sizeLengthToggle: sizeLengthToggleCopy,
        }),
      );
      if (res) {
        for (let i = 0; i < mergedStates.length; i++) {
          const isNew = !landedInfoStates.some((s) => s.distributionCentre?.distributionName === mergedStates[i]?.distributionCentre?.distributionName);
          if (isNew) {
            await handleLandedInfoModule(
              res as LandedTemplateArg,
              mergedStates[i] as LandedInfoStateEntry,
              grandTotalState.grandTotal as LandedGrandTotalArg,
              productInfo?.incoterms as unknown as LandedIncotermArg,
              i,
            );
          }
        }
      }
    }
  };

  /**
   * Builds the exchange-rate block persisted for one distribution centre.
   * `companyCurrency` is the distribution centre's own currency, not the company's: every reader
   * treats it that way — getRetailExRate here, and resolveDcDefaultRate / resolveRetailExRate in the
   * backend recompute — so stamping the company currency made multi-centre costings disagree with
   * the server. `companyCurrencyValue` is that currency's base value from the season master, which
   * replaces a document-root field the multiple-currencies migration removed.
   * @param dcExchangeInfo the distribution centre's current exchange-rate block.
   * @param dcState the distribution centre's landed state.
   * @returns the block to persist.
   */
  const buildDcExchangeInfo = (dcExchangeInfo: DcExchangeInfo | undefined, dcState: LandedInfoStateEntry): DcExchangeInfo => {
    const dcCurrency = dcState?.distributionCentre?.currency || userInfo?.companyCurrency;
    const companyCurrencyValue = resolveCompanyCurrencyValue(productExchnageRate?.exchangeRateList, dcCurrency);
    if (!isEmpty(dcExchangeInfo)) {
      return {
        ...dcExchangeInfo,
        exchangedRateManualFalse: internalState?.primaryCurrValue || 0,
        companyCurrency: dcCurrency,
        supplierCurrency: productInfo?.currency as string,
        companyCurrencyValue,
      };
    }
    return {
      exchangedRate: internalState?.primaryCurrValue || 1,
      exchangedRateManualFalse: internalState?.primaryCurrValue || 0,
      companyCurrency: dcCurrency,
      supplierCurrency: productInfo?.currency as string,
      companyCurrencyValue,
      manualAdded: false,
    };
  };

  const createdSaveDataObj = async (): Promise<CostingSavePayload> => {
    const getCombinationsFromCostingData = (): ExtractedCombinations => {
      if (averageCostingActive && (averageCosting?.averageCostingInfo?.length ?? 0) > 0) {
        return extractCombinationsFromCostingData(averageCosting.averageCostingInfo as CostingCombinationRow[]);
      }
      if (detailedCostingActive && (detailedQuanitiesState?.quantities?.quantitiesInfo?.length ?? 0) > 0) {
        return extractCombinationsFromCostingData(detailedQuanitiesState.quantities.quantitiesInfo as CostingCombinationRow[]);
      }

      return {
        colourways: (colourWayToggle ? productColours?.map((vl) => vl?._id) : []) as string[],
        lengths: (sizeLengthToggle ? productLengths?.map((vl) => vl?._id) : []) as string[],
        sizes: (() => {
          const shouldIncludeSizes = (!colourWayToggle && !sizeLengthToggle) || sizeLengthToggle;
          if (shouldIncludeSizes) {
            return (costingSizes?.length > 0 ? costingSizes : productSizes) as string[];
          }
          return [];
        })(),
      };
    };

    const { colourways: extractedColourways, lengths: extractedLengths, sizes: extractedSizes } = getCombinationsFromCostingData();
    const responsePayload: CostingSavePayload = {
      markAsComplete: internalState?.markAsComplete,
      devOrderId,
      isAverageCosting: averageCostingActive,
      isDetailedCosting: detailedCostingActive,
      status,
      colourWayToggle,
      sizeLengthToggle,
      colourways: extractedColourways,
      lengths: extractedLengths,
      sizes: extractedSizes,
      productInfo: {
        supplierId: (productInfo?.supplierId as unknown as SupplierRef)?._id,
        currency: productInfo?.currency,
        incoterms: (productInfo?.incoterms as unknown as { _id?: string })?._id,
        reference: productInfo?.reference?.trim(),
        shipmentMethod: productInfo?.shipmentMethod,
        targetCost: productInfo?.targetCost,
        factoryId: (productInfo?.factoryId as unknown as { _id?: string })?._id,
        moqNeeded: productInfo?.moqNeeded,
      },
      sampleRef: (sampleRef as { _id?: string })?._id ?? null,
      productTemplate: productTemplate as PageTemplateDetail,
      averageCosting: {
        averageCostingInfo: await manipualtedProductCostingSaveData(averageCosting.averageCostingInfo as SaveDataRows, 'averageCosting'),
        sumOfTotalQuantity: averageCosting.sumOfTotalQuantity || '0',
        totalPricePerUnit: averageCosting.totalPricePerUnit || '0',
        overAllSumOfQuantityPerSizCol: averageCosting.overAllSumOfQuantityPerSizCol || '0',
        overAllSumOfTotalPrice: averageCosting.overAllSumOfTotalPrice || '0',
        overAllSumOfPricePerUnit: averageCosting.overAllSumOfPricePerUnit || '0',
        totLength: averageCosting?.totLength,
        totSize: averageCosting?.totSize,
        totColourways: averageCosting?.totColourways,
      },
      testing: {
        testingInfo: await manipualtedProductCostingSaveData(testingState?.testing?.testingInfo as SaveDataRows, 'testing'),
        sumOfPrice: testingState?.testing?.sumOfPrice || '0',
      },
      grandTotal: {
        grandTotalInfo: await manipualtedProductCostingSaveData(grandTotalState?.grandTotal as SaveDataRows, 'grandTotal'),
        totalBomTypeTotalPrice: grandTotalState?.totalBomTypeTotalPrice,
        totalOverAllSumTotalPrice: grandTotalState?.totalOverAllSumTotalPrice,
      },
      landedInformation: { landedInfoArr: await manipualtedProductCostingSaveData(landedInfoState?.landedInfo?.landedInfoArr as SaveDataRows, 'landedInfo') },
      landedInformations: await Promise.all(
        (landedInfoStates.length > 0 ? landedInfoStates : []).map(async (dcState) => ({
          distributionCentreId: dcState.distributionCentre?._id || null,
          distributionCentreName: dcState.distributionCentre?.distributionName,
          distributionCentreCurrency: dcState.distributionCentre?.currency,
          landedExchangeRateInformation: buildDcExchangeInfo(dcState.landedExchangeRateInformation as DcExchangeInfo, dcState),
          landedInfoArr: await manipualtedProductCostingSaveData(dcState.landedInfo?.landedInfoArr as SaveDataRows, 'landedInfo'),
        })),
      ),
      detailedCosting: [],
      quantities: [],
    };
    const liveTemplate =
      costingTemplateDetail && productTemplate && costingTemplateDetail._id === productTemplate._id ? costingTemplateDetail : productTemplate;
    if (landedInformationColumns?.length && productCostingDetail) {
      responsePayload.productTemplate.landedInfo = landedInformationColumns as TemplateColumn[];
    } else if (productCostingDetail) {
      responsePayload.productTemplate.landedInfo = landedInformationColumns as TemplateColumn[];
    } else {
      responsePayload.productTemplate.landedInfo = productTemplate?.landedInfo as TemplateColumn[];
    }
    responsePayload.productTemplate.landedInfo = (responsePayload.productTemplate.landedInfo || []).map((col) => {
      if (col?.aliasDisplayName && col?.aliasName) return col;
      const liveCol = liveTemplate?.landedInfo?.find((lc) => String(lc?._id) === String(col?._id));
      return {
        ...col,
        aliasName: col?.aliasName || liveCol?.aliasName,
        aliasDisplayName: col?.aliasDisplayName || liveCol?.aliasDisplayName,
      };
    });
    responsePayload.productTemplate.isCostingInfoActive = liveTemplate?.isCostingInfoActive ?? false;
    const enteredCostingInfoValues = internalState?.costingInfoValues || {};
    responsePayload.productTemplate.costingInfo = (liveTemplate?.costingInfo || productTemplate?.costingInfo || [])
      .filter((item) => !item?.isDefault)
      .map((item) => ({ ...item, value: enteredCostingInfoValues[item?._id as string] ?? item?.value ?? null }));
    if (productTemplate?.isAdditionalComponentActive) {
      responsePayload.additionalComponent = {
        additionalComponentInfo: await manipualtedProductCostingSaveData(
          additionalComponentState?.additionalComponent?.additionalComponentInfo as SaveDataRows,
          'additional',
        ),
        sumOfTotPrice: additionalComponentState?.additionalComponent?.sumOfTotPrice || '0',
      };
    } else {
      responsePayload.additionalComponent = {
        additionalComponentInfo: [],
        sumOfTotPrice: '0',
      };
    }
    if (productTemplate?.isSetUpCostActive) {
      responsePayload.additional = {
        additionalInfo: await manipualtedProductCostingSaveData(additionalState?.additional?.additionalInfo as SaveDataRows, 'additional'),
        sumOfTotPrice: additionalState?.additional?.sumOfTotPrice || '0',
      };
    } else {
      responsePayload.additional = {
        additionalInfo: [],
        sumOfTotPrice: '0',
      };
    }
    /* Rows carry no references to flatten, so they go straight through rather than via
       `manipualtedProductCostingSaveData`. The else branch clears the section rather than omitting
       it, matching the two above — omitting it would leave stale rows behind on a toggled-off
       section, because the endpoint `$set`s whatever the body contains. */
    if (productTemplate?.isSpecificationRequirementActive) {
      responsePayload.specificationRequirement = {
        specificationRequirementInfo: withoutEmptyRequirements(specificationRequirementState?.specificationRequirement?.specificationRequirementInfo),
      };
    } else {
      responsePayload.specificationRequirement = {
        specificationRequirementInfo: [],
      };
    }
    if (averageCostingActive) {
      responsePayload.averageCosting = {
        averageCostingInfo: await manipualtedProductCostingSaveData(averageCosting.averageCostingInfo as SaveDataRows, 'averageCosting'),
        sumOfTotalQuantity: averageCosting.sumOfTotalQuantity || '0',
        totalPricePerUnit: averageCosting.totalPricePerUnit || '0',
        overAllSumOfQuantityPerSizCol: averageCosting.overAllSumOfQuantityPerSizCol || '0',
        overAllSumOfTotalPrice: averageCosting.overAllSumOfTotalPrice || '0',
        overAllSumOfPricePerUnit: averageCosting.overAllSumOfPricePerUnit || '0',
        totLength: averageCosting?.totLength,
        totSize: averageCosting?.totSize,
        totColourways: averageCosting?.totColourways,
      };
      responsePayload.detailedCosting = {
        bomTypeArr: [],
      };
      responsePayload.quantities = {
        quantitiesInfo: [],
        sumOfTotalQuantity: 0,
        totalPricePerUnit: 0,
        sumOfQuantityPerSizCol: 0,
        overAllSumOfQuantityPerSizCol: 0,
        totLength: [],
        totSize: [],
        totColourways: [],
      };
    } else if (detailedCostingActive) {
      responsePayload.averageCosting = {
        averageCostingInfo: [],
        sumOfTotalQuantit: '0',
        totalPricePerUni: '0',
        overAllSumOfQuantityPerSizCo: '0',
        overAllSumOfTotalPric: '0',
        overAllSumOfPricePerUni: '0',
        totLengt: [],
        totSiz: [],
        totColourway: [],
      };
      responsePayload.detailedCosting = {
        bomTypeArr: await manipualtedProductCostingSaveData(bomTypeArr as SaveDataRows, 'detailedCosting'),
      };
      responsePayload.quantities = {
        quantitiesInfo: await manipualtedProductCostingSaveData(detailedQuanitiesState?.quantities?.quantitiesInfo as SaveDataRows, 'quantities'),
        sumOfTotalQuantity: detailedQuanitiesState?.quantities?.sumOfTotalQuantity || '0',
        totalPricePerUnit: detailedQuanitiesState?.quantities?.totalPricePerUnit || '0',
        sumOfQuantityPerSizCol: detailedQuanitiesState?.quantities?.sumOfQuantityPerSizCol || '0',
        overAllSumOfQuantityPerSizCol: detailedQuanitiesState?.quantities?.overAllSumOfQuantityPerSizCol || '0',
        totLength: detailedQuanitiesState?.quantities?.totLength,
        totSize: detailedQuanitiesState?.quantities?.totSize,
        totColourways: detailedQuanitiesState?.quantities?.totColourways,
      };
    }
    return responsePayload;
  };

  const openSubmitModal = (): void => {
    if ((status === partiallyComplete || status === 'Pending') && effectiveCostingId) {
      if (!checkValidation()) {
        setInternalState((state) => ({
          ...state,
          productCostCommentModal: true,
        }));
      } else toasterMsg(pleaseFillTheRequiredFields, 'danger');
    } else {
      saveCosting();
    }
  };

  const saveCosting = async (): Promise<boolean | undefined> => {
    setCostingLoader(true);
    for (const dcState of landedInfoStates) {
      if (dcState?.landedExchangeRateInformation?.manualAdded === true && !dcState?.landedExchangeRateInformation?.exchangedRate) {
        toasterMsg(`Please add manual exchange rate value for ${dcState?.distributionCentre?.distributionName || 'distribution centre'}`, 'danger');
        setCostingLoader(false);
        return false;
      }
    }
    if (!checkValidation()) {
      const data = await createdSaveDataObj();
      setState((prevState) => ({ ...prevState, modalLoading: true }));
      if (effectiveCostingId) {
        data._id = effectiveCostingId;
        data.message = internalState?.commentMsg;
        data.devOrderId = devOrderId || queryDevOrderId;
        if (isGlobalUrl && querySupplierId) {
          data.modifiedBy = querySupplierId;
        }
        if (isSupplierView && !internalState.markAsComplete) {
          data.status = partiallyComplete;
        } else if (
          !internalState.markAsComplete &&
          ['Pending', partiallyComplete]?.includes(status) &&
          (productCostingDetail?.productInfo?.supplierId as { _id?: string } | undefined)?._id !== supplierId?._id
        ) {
          data.status = 'Draft';
        } else {
          data.status = internalState?.markAsComplete && status !== 'Draft' ? 'Complete' : status;
        }
        const res = await dispatch(updateProductCostingData(data, t as unknown as TransactionMsg));
        if (res) {
          if (isGlobalUrl) {
            dispatch(
              getUnIntegratedSupplierCostingDetailsById({
                productId: queryCostingId,
                devOrderId: queryDevOrderId,
                supplierId: querySupplierId,
              }) as never,
            );
          } else {
            dispatch(getProductCostingDetailsById(productid, t as unknown as TransactionMsg));
          }
        }
        setInternalState((state) => ({
          ...state,
          productCostCommentModal: false,
          commentMsg: '',
        }));

        setCostingLoader(false);
        setState((prevState) => ({ ...prevState, modalLoading: false }));
      } else {
        const res = await dispatch(addProductCostingData(data, t as unknown as TransactionMsg));
        if (res) {
          history.push(costingDetailPath(devOrderId, (res as { _id?: string })._id));
          setState((prevState) => ({ ...prevState, modalLoading: false }));
          setInternalState((state) => ({
            ...state,
            productCostCommentModal: false,
            commentMsg: '',
          }));
          setCostingLoader(false);
        }
      }
    } else {
      setCostingLoader(false);
      setInternalState((state) => ({
        ...state,
        productCostCommentModal: false,
      }));
      setState((prevState) => ({ ...prevState, modalLoading: false }));

      toasterMsg(pleaseFillTheRequiredFields, 'danger');
    }
    return undefined;
  };

  const requestInvite = async (): Promise<void> => {
    if (productid) {
      setInternalState((state) => ({
        ...state,
        productCostCommentModal: true,
        inviteBtn: true,
        commentMsg: '',
      }));
    } else toasterMsg('Please fill the required fields', 'danger');
  };

  const inviteSupplier = async (): Promise<void> => {
    const copyState: ProductCostingState = cloneDeep(productCostingstate);
    if (supplierId?._id) {
      const obj = {
        supplierId: supplierId?._id,
        status: internalState?.markAsComplete ? 'Complete' : 'Pending',
        _id: productid,
        message: internalState?.commentMsg,
        devOrderId,
        defaultMsg: `${userInfo?.firstName} ${userInfo?.lastName} has invited supplier`,
        markAsComplete: internalState?.markAsComplete,
      };
      setState((prevState) => ({ ...prevState, modalLoading: true }));
      const res = await dispatch(inviteSupplierAction(obj, t as unknown as TransactionMsg));
      if (res === 500) {
        setState((prevState) => ({ ...prevState, modalLoading: false }));
        setInternalState((state) => ({
          ...state,
          productCostCommentModal: false,
          inviteBtn: false,
          commentMsg: '',
        }));
      } else if (res) {
        set(copyState, commonPath().STATUS, (res as { status?: string }).status);
        setState((prevState) => ({ ...prevState, modalLoading: false }));
        setInternalState((state) => ({
          ...state,
          productCostCommentModal: false,
          inviteBtn: false,
          commentMsg: '',
        }));
        history.push(costingListPath(devOrderId));
      }
    }
  };

  useEffect(() => {
    return () => {
      unmountCostingState();
    };
  }, []);

  const handleToggleStateCosting = async (): Promise<void> => {
    hasUserToggledCostingTabRef.current = true;
    const copyState: ProductCostingState = cloneDeep(productCostingstate);
    const copyStateDetailedCosting = cloneDeep(detailedCostingState);
    const copyStateDetailedQuanities = cloneDeep(detailedQuanitiesState);
    let rebuildAverageCosting = false;

    switch (compState?.tabName) {
      case 'averageCosting':
        {
          if (subModuleActiveTab !== compState?.tabName) {
            set(copyState, averageCostingFieldpath().AVERAGE_COSTING_ACTIVE, true);
            rebuildAverageCosting = true;
            set(initialDetailedCostingState, detailedCostingFieldpath().DETAILED_COSTING_ACTIVE, false);
            setDetailedCostingState((prevState) => ({ ...prevState, ...initialDetailedCostingState }));
            setDetailedQuanitiesState((prevState) => ({ ...prevState, ...initialDetailedCostingQuanitiesState }));
          }
        }
        break;
      case 'detailedCosting':
        {
          if (bomCompoList?.length === 0) {
            toasterMsg(t('DEVLOPMENT_ORDER.COSTING_DETAILS.BOM_COMPONENT_PRODUCT'), 'danger');
            return;
          }

          if (subModuleActiveTab !== compState?.tabName) {
            set(copyState, averageCostingFieldpath().AVERAGE_COSTING_ACTIVE, false);
            set(copyStateDetailedQuanities, quantitiesFieldpath().QUANTITES_INFO, []);
            set(copyState, averageCostingFieldpath().AVERAGE_COSTING_INFO, []);
            set(copyState, averageCostingFieldpath().TOTAL_AVERAGE_SIZES, []);
            set(copyState, averageCostingFieldpath().TOTAL_AVERAGE_LENGTH, []);
            set(copyState, averageCostingFieldpath().TOTAL_AVERAGE_COLOURWAYS, []);
            set(copyState, averageCostingFieldpath().EXPAND_OVERALL_SUM_TOTAL_PRICE, '0');
            set(copyState, averageCostingFieldpath().EXPAND_OVERALL_SUM_QUANTITY_PER_COL_SIZ, '0');
            set(copyState, averageCostingFieldpath().EXPAND_OVERALL_SUM_PRICE_PER_UNIT, '0');
            set(copyStateDetailedCosting, detailedCostingFieldpath().DETAILED_COSTING_DATA, []);
            await handleQuantities(!productid && (productColours?.length ?? 0) > 0, false);
            await renderDetailedCostingArr(
              initialDetailedCostingState?.detailedCosting?.bomTypeArr as BomListArg,
              true,
              !productid && (productColours?.length ?? 0) > 0,
              false,
            );
          }
        }
        break;
      default:
        set(copyState, averageCostingFieldpath().AVERAGE_COSTING_ACTIVE, true);
        set(copyState, detailedCostingFieldpath().DETAILED_COSTING_ACTIVE, false);
        break;
    }
    setActivateColSizeLengthToggle((prev) => ({ ...prev, colourWayToggle: !productid && (productColours?.length ?? 0) > 0, sizeLengthToggle: false }));
    setProductCostingState((prevState) => ({ ...prevState, ...copyState }));
    if (rebuildAverageCosting) {
      await handleAverageCosting(!productid && (productColours?.length ?? 0) > 0, false);
    }
    setTestingState((prev) => ({ ...prev, ...initialTestingState }));
    setGrandTotalState((prev) => ({
      ...prev,
      grandTotal: [],
      totalBomTypeTotalPrice: '0',
      totalOverAllSumTotalPrice: '0',
    }));
    if (costingTemplateDetail?._id) {
      const newColourWayToggle = !productid && (productColours?.length ?? 0) > 0;
      const templateRes = await dispatch(
        getProductCostingTemplateDetailsByIds(costingTemplateDetail._id, devOrderId, {
          colourWayToggle: newColourWayToggle,
          sizeLengthToggle: false,
        }),
      );
      if (templateRes) {
        await handleAdditonalModule(templateRes as LandedTemplateArg, additionalState);
        await handleAdditonalComponentModule(templateRes as LandedTemplateArg, additionalComponentState);
        handleSpecificationRequirementModule(templateRes as LandedTemplateArg);
        setInternalState((prev) => ({ ...prev, currSelectedTemplateDetails: templateRes as CostingTemplateDetail }));
        if (productInfo?.incoterms) {
          if (landedInfoStates.length > 0) {
            for (let i = 0; i < landedInfoStates.length; i++) {
              const freshDcState = {
                ...landedInfoStates[i],
                landedInfo: { landedInfoColumnArr: [], landedInfoArr: [], currency: null, targetCost: '' },
              } as LandedInfoStateEntry;
              await handleLandedInfoModule(templateRes as LandedTemplateArg, freshDcState, [] as LandedGrandTotalArg, undefined, i);
            }
          } else {
            await handleLandedInfoModule(templateRes as LandedTemplateArg, [] as unknown as LandedInfoStateEntry, [] as LandedGrandTotalArg);
          }
        }
      }
    }
    setDefaultTabs((prev) => ({ ...prev, [compState?.module]: compState?.tabName }));

    setState((prevState) => ({
      ...prevState,
      showHideAlertPopup: false,
    }));
  };

  useEffect(() => {
    const callFn = async (): Promise<void> => {
      await dispatch(refreshModule(false));
    };
    if (isRefreshed) {
      callFn();

      const colourWayToggleCopy = productCostingDetail?.colourWayToggle;
      const sizeLengthToggleCopy = productCostingDetail?.sizeLengthToggle;
      let hideColourCol = false;
      let hideSizeCol = false;
      let hideLengthCol = false;

      if (productCostingDetail?.colourWayToggle) {
        hideColourCol = (productColours?.length ?? 0) > 0;
      }
      if (productCostingDetail?.sizeLengthToggle) {
        hideSizeCol = (productSizes?.length ?? 0) > 0;
        hideLengthCol = (productLengths?.length ?? 0) > 0;
      }
      setInternalState((state) => ({
        ...state,
        hideColourCol,
        hideSizeCol,
        hideLengthCol,
      }));
      setActivateColSizeLengthToggle((prev) => ({
        ...prev,
        colourWayToggle: colourWayToggleCopy as boolean,
        sizeLengthToggle: sizeLengthToggleCopy as boolean,
      }));
      refreshAllModules(colourWayToggleCopy as boolean, sizeLengthToggleCopy as boolean);
      refreshDistributionCentresList(colourWayToggleCopy, sizeLengthToggleCopy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshed, dispatch, devOrderId, productCostingDetail]);

  const clearHighlight = async (): Promise<void> => {
    if (productid) {
      const res = await dispatch(clearAllProductCostingSuggestedHighlights(productid, supplierChangeKeysArray, t as unknown as TransactionMsg));
      if (res) {
        dispatch(clearedAllStatus(true));
      }
    }
  };

  const currentCostingObject = useMemo(
    () => ({
      _id: productid,
      reference,
      supplier: supplierId?._id,
      supplierName: {
        supplierId: supplierId?._id,
        supplierName: supplierId?.name || 'Supplier',
      },
      status: productCostingDetail?.status,
    }),
    [productid, reference, supplierId?._id, supplierId?.name, productCostingDetail?.status],
  );

  const costingListForChat = useMemo(() => [currentCostingObject], [currentCostingObject]);

  const recalculateForAutomatedFreightRef = useRef(recalculateLandedInfoForAutomatedFreight);
  recalculateForAutomatedFreightRef.current = recalculateLandedInfoForAutomatedFreight;

  useEffect(() => {
    if (automatedFreight) recalculateForAutomatedFreightRef.current();
  }, [automatedFreight]);

  if (isAccessDenied) {
    return <p className="mt-5 text-center">{t('DEVLOPMENT_ORDER.PRODUCT_COSTING_INDEX.DETAILS_NOT_AVAILBLE')}</p>;
  }

  return (
    <>
      <div className="page-title-filter sticky">
        <div className="title-container d-flex align-items-center">
          <ProductImageThumb src={imgSrc(productDetails)} alt={productName?.number} />
          <h4 className="title ml-3 edit-product-icon">
            {t('DEVLOPMENT_ORDER.PRODUCT_COSTING_INDEX.PRODUCT_COSTING')}
            {productName?.number ? <>&nbsp;|&nbsp;{shortenandHoverString(productName?.number, 15)}</> : null}
            {productName?.description ? <>&nbsp;|&nbsp;{shortenandHoverString(productName?.description, 40)}</> : null}
          </h4>
        </div>
        <div className="search-filter">
          {(!isSupplierView || isGlobalUrl) && effectiveCostingId && (
            <CustomTooltip tooltipText={t('RANGE_PLANS.PRODUCT_LIST.RP_DOWNLOAD_SHEET')}>
              <button type="button" className="btn p-0 d-inline-flex ml-8" aria-label="Download" onClick={() => handleExcelDownload()}>
                <DownloadIcon size={24} />
              </button>
            </CustomTooltip>
          )}
          {hideInviteBtn && !isProductArchived && (
            <CustomTooltip tooltipText={t('ADD_PO_TEMPLATE_PROVIDER.INVITE_SUPPLIER')}>
              <button type="button" className="btn p-0 d-inline-flex ml-8" onClick={() => requestInvite()} aria-label="button">
                <InviteSupplierIcon size={24} />
              </button>
            </CustomTooltip>
          )}

          {CoastingAddEditRights && !isSupplierView && productid && internalState?.isChangedBySupllierState && (
            <CustomTooltip tooltipText={t('DEVLOPMENT_ORDER.MEASUREMENT.MEASUREMENTS.CLEAR_HIGHLIGHT')}>
              <button className="bombiix-icon-container ml-8" aria-label="clear" type="button" onClick={() => clearHighlight()}>
                <ClearHighlightsIcon size={24} />
              </button>
            </CustomTooltip>
          )}

          <button className="btn btn-main-titlebar btn-blue bombiix-btn ml-8" type="button" aria-label="Save" onClick={() => openSubmitModal()}>
            {t('COMMON_LABEL.SAVE')}
          </button>

          {!isGlobalUrl && (
            <button
              type="button"
              className="btn btn-main-titlebar btn-light bombiix-btn ml-8"
              onClick={() => history.push(costingListPath(devOrderId))}
              aria-label="Back"
            >
              {t('COMMON_LABEL.BACK') || 'Back'}
            </button>
          )}
        </div>
      </div>
      <Container fluid className="common-comment-container">
        <div className="d-flex align-items-center ml-auto">
          {!isSupplierView && (
            <ProductCostingTabMenu
              activeTheTab={(val: string) => activeTheTab(val, 'subModuleActiveTab')}
              masterActiveTab={subModuleActiveTab}
              MenuList={SubModuleTabs}
              isAPILoading={false}
              isNormalSwitch
              underlineClassName={subModuleActiveTab === 'averageCosting' ? 'tab-underline-average' : 'tab-underline-detailed'}
            />
          )}
          {hideSection && !isSupplier && !isGlobalUrl && (
            <div className="common-checkbox bold ml-auto">
              <input
                onChange={(e) => {
                  setInternalState((prev) => ({
                    ...prev,
                    markAsComplete: e.target.checked,
                  }));
                }}
                name="markAsComplete"
                id="markAsComplete"
                type="checkbox"
                checked={internalState?.markAsComplete}
                disabled={isProductArchived || !CoastingAddEditRights}
              />
              <label htmlFor="markAsComplete" className="chklabel text-nowrap">
                <span>{t('DEVLOPMENT_ORDER.PRODUCT_COSTING.COSTING_CLOSED')}</span>
              </label>
            </div>
          )}
        </div>

        {(() => {
          if (loader) {
            return <Loader />;
          }

          if (moduleActiveTab === 'productCosting') {
            return <ProductCosting />;
          }

          if (moduleActiveTab === 'comments') {
            return commentLoader ? <Loader /> : <ProductCostingComment />;
          }

          return null;
        })()}
      </Container>
      {internalState.productCostCommentModal && (
        <SubmitModal
          isOpen={internalState.productCostCommentModal}
          title={internalState.inviteBtn ? 'Invite Supplier' : 'Submit Changes'}
          onChange={(e) => {
            setInternalState((state) => ({
              ...state,
              commentMsg: e.target.value,
            }));
            e.preventDefault();
          }}
          onConfirm={() => (internalState.inviteBtn ? inviteSupplier() : saveCosting())}
          loading={compState.modalLoading}
          commentMsg={internalState.commentMsg}
          handleModal={() =>
            setInternalState((state) => ({
              ...state,
              productCostCommentModal: false,
              inviteBtn: false,
              commentMsg: '',
            }))
          }
        />
      )}
      {compState.showHideAlertPopup && (
        <BombiixModal
          className="bombiix-modal--top"
          size="sm"
          show={compState.showHideAlertPopup}
          title={t('ALERT_POPUP_VIEW.PRODUCT_COSTING.COSTING.TITLE')}
          saveLabel={t('CONFIRMATION_POPUP.CONFIRM')}
          saveVariant="red"
          onSave={() => {
            handleToggleStateCosting();
          }}
          onClose={() =>
            setState((prevState) => ({
              ...prevState,
              showHideAlertPopup: !prevState.showHideAlertPopup,
            }))
          }
        >
          <p className="costing-confirm__message">{t('ALERT_POPUP_VIEW.PRODUCT_COSTING.COSTING.MESSAGE')}</p>
          <p className="costing-confirm__warning">{t('ALERT_POPUP_VIEW.PRODUCT_COSTING.COSTING.DESCRIPTION')}</p>
        </BombiixModal>
      )}
      {productid && <Chat moduleKey="Costing" moduleList={costingListForChat} isDetailsPage currentUser={userInfo} />}
    </>
  );
}

export default NewProducCostingDetailPage;
